import dayjs from 'dayjs'
import { PromotionParam } from 'src/models/Promotion'
import { RecommendedUser } from 'src/models/User'
import useSWR, { mutate, SWRConfiguration } from 'swr'
import { ApiClient, fetcher } from './client'
import {
  GET_RECOMMENDATIONS,
  POST_INTERESTED,
  POST_NOT_INTERESTED,
} from './endpoints'
import { Promotion } from './types/promotion'
import {
  PostInterestedRequest,
  PostInterestedResponse,
  PostNotInterestedRequest,
  RecommendedCompanies,
  RecommendedCompany,
} from './types/recommendation'

const recommendedUserFetcher = async (url: string) => {
  const data: RecommendedCompanies = await fetcher(url)
  return data.companies
    .map((each) => toRecommendedUser(each))
    .concat(
      data.promotion
        ? toRecommendedUser(data.promotion.company, data.promotion)
        : [],
    )
}

export const useRecommendations = (config?: Partial<SWRConfiguration>) =>
  useSWR<RecommendedUser[]>(GET_RECOMMENDATIONS, recommendedUserFetcher, config)

const recommendationOptimisticMutator =
  (id: number, status: RecommendedCompany['matchingStatus']) =>
  (data: RecommendedUser[]) => {
    const target = data?.find((each) => each.id === id)
    if (!target) return data
    const updated: RecommendedUser = {
      ...target,
      matchingStatus: status,
    }
    return data.map((each) => {
      if (each.id !== id) return each
      else return updated
    })
  }

export const postInterested = async (
  userId: number,
  requestReason?: string,
  promotionParam?: PromotionParam,
  detail?: string,
  premiumContributionId?: number,
  exhibitionId?: number,
) => {
  const body: PostInterestedRequest = {
    request_box: {
      reply_company_id: userId,
      request_reason: requestReason,
      promotion_id: promotionParam?.id,
      authentication_token: promotionParam?.token,
      detail: detail,
    },
    premium_contribution_id: premiumContributionId,
    exhibition_id: exhibitionId,
  }
  const res = await ApiClient.post<PostInterestedResponse>(
    POST_INTERESTED,
    body,
  )
  if (!res.error && res.data)
    mutate(
      GET_RECOMMENDATIONS,
      recommendationOptimisticMutator(userId, res.data.result),
      false,
    )
  return res
}

export const postNotInterested = async (
  userId: number,
  promotionParam?: PromotionParam,
) => {
  const body: PostNotInterestedRequest = {
    not_interest_company_id: userId,
    promotion_id: promotionParam?.id,
    authentication_token: promotionParam?.token,
  }
  const res = await ApiClient.post(POST_NOT_INTERESTED, body)
  if (!res.error)
    mutate(
      GET_RECOMMENDATIONS,
      recommendationOptimisticMutator(userId, 'not_interest'),
      false,
    )
  return res
}

export const toRecommendedUser = (
  data: RecommendedCompany,
  promotion?: Pick<
    Promotion,
    'id' | 'authenticationToken' | 'prDetail' | 'displayType'
  >,
): RecommendedUser => {
  const typeOfBusinessArray = Array.isArray(data.typeOfBusiness)
    ? data.typeOfBusiness
    : data.typeOfBusiness
    ? [data.typeOfBusiness]
    : null

  return {
    id: data.id,
    canInterst: data.canInterestOrNot,
    isActive: true || data.isActive,
    isNew: data.isNew,
    isCertified: data.isCertified,
    name: data.adminName || '',
    companyName: data.name || '',
    position: data.position || '',
    capital: data.capitalText,
    businessType: typeOfBusinessArray?.join('、') || '',
    foundedOn: data.foundedAt ? `${dayjs(data.foundedAt).year()}` : undefined,
    imageSrc:
      typeof data.imageUrl === 'string'
        ? data.imageUrl
        : data.imageUrl?.medium?.url || data.imageUrl?.url,
    numberOfEmployees: data.employeeNumberText,
    prefecture: data.prefecture,
    matchingStatus: data.matchingStatus,
    roomUrl: data.roomUrl,
    promotion: promotion
      ? {
          id: promotion.id,
          token: promotion.authenticationToken,
          prDetail: promotion.prDetail,
          displayType: promotion.displayType,
        }
      : undefined,
    specialties: data.specialties,
    adminAges: data.adminAges,
    typeOfBusiness: data.typeOfBusiness,
    feedbackScore: data.feedbackScore,
    feedbackCount: data.feedbackCount,
  }
}
